import type { ReactNode } from 'react';

export interface AvatarProps {
  name: string;
  imageUrl?: string | null;
  /** xs is the 24px Inbox row size (redesign spec §Inbox). */
  size?: 'xs' | 'sm' | 'md';
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}

export function Avatar({ name, imageUrl, size = 'md' }: AvatarProps) {
  if (imageUrl) {
    return <img className={`ui-avatar ui-avatar--${size}`} src={imageUrl} alt={name} />;
  }
  return (
    <span className={`ui-avatar ui-avatar--${size}`} role="img" aria-label={name}>
      {initialsOf(name)}
    </span>
  );
}

export type BadgeTone =
  'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'review' | 'revision';

/**
 * `micro` is the smaller chip the redesign spec asks for in dense rows —
 * Inbox event badges and the Companies key-value rows. Same tones, less ink.
 */
export function Badge({
  tone = 'neutral',
  micro = false,
  children,
}: {
  tone?: BadgeTone;
  micro?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`ui-badge ui-badge--${tone}${micro ? ' ui-badge--micro' : ''}`}>
      {children}
    </span>
  );
}

export interface PanelProps {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export function Panel({ title, actions, children }: PanelProps) {
  return (
    <section className="ui-panel">
      {title || actions ? (
        <header className="ui-panel-header">
          {title ? <h2>{title}</h2> : <span />}
          {actions ? <div className="ui-panel-actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="ui-panel-body">{children}</div>
    </section>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="ui-empty" role="status">
      <p className="ui-empty-title">{title}</p>
      {description ? <p className="ui-empty-description">{description}</p> : null}
      {action ? <div className="ui-empty-action">{action}</div> : null}
    </div>
  );
}

export function Skeleton({
  lines = 3,
  label = 'Loading content',
}: {
  lines?: number;
  label?: string;
}) {
  return (
    <div className="ui-skeleton" role="status" aria-label={label}>
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} className="ui-skeleton-line" aria-hidden="true" />
      ))}
    </div>
  );
}
